import { Box, Stack } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export default function DateRange({
  margin = 2,
  spacing = 1,
  divider = true,
  variant = "outlined",
  paramsStart,
  paramsEnd,
  pageParam = null,
  testId,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  const { t } = useTranslation();

  function handleStartDate(val) {
    const value = dayjs(val).format("DD/MM/YYYY");
    if (value !== "Invalid Date" && !isFieldEmpty(value)) {
      params.set(paramsStart, value);
    } else {
      params.delete(paramsStart);
    }
    if (pageParam) {
      params.set(pageParam, 0);
    }
    setSearchParams(params);
  }

  function handleEndDate(val) {
    const value = dayjs(val).format("DD/MM/YYYY");
    if (value !== "Invalid Date" && !isFieldEmpty(value)) {
      params.set(paramsEnd, value);
    } else {
      params.delete(paramsEnd);
    }
    if (pageParam) {
      params.set(pageParam, 0);
    }
    setSearchParams(params);
  }

  function isFieldEmpty(field) {
    return field == "" || field == null || field == undefined;
  }

  const checkOverlap = (startDate, endDate) => {
    return dayjs(startDate).isAfter(dayjs(endDate));
  };

  const startDate = dayjs(params.get(paramsStart), "DD/MM/YYYY").isValid()
    ? dayjs(params.get(paramsStart), "DD/MM/YYYY")
    : null;
    
  const endDate = dayjs(params.get(paramsEnd), "DD/MM/YYYY").isValid()
    ? dayjs(params.get(paramsEnd), "DD/MM/YYYY")
    : null;

  return (
    <Stack
      direction={"row"}
      spacing={spacing}
      sx={{ width: "100%", m: margin }}
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <MobileDatePicker
        format="DD/MM/YYYY"
        sx={{ width: "100%", m: margin }}
        value={startDate}
        label={t("StartDate")}
        onAccept={(value) => {
          if (checkOverlap(value, endDate)) {
            handleEndDate(null);
          }
          handleStartDate(value);
        }}
        slotProps={{
          textField: {
            size: "small",
            variant: variant,
            ...(testId && { inputProps: { "data-testid": `${testId}.start-input` } }),
          },
          actionBar: { actions: ["clear", "cancel", "accept"] },
          ...(testId && {
            previousIconButton: { "data-testid": `${testId}.start-input.prev-month` },
            nextIconButton: { "data-testid": `${testId}.start-input.next-month` },
            day: (ownerState) => ({
              "data-testid": `${testId}.start-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
            }),
          }),
        }}
      />
      {divider ? <Box>-</Box> : null}
      <MobileDatePicker
        format="DD/MM/YYYY"
        value={endDate}
        minDate={startDate}
        sx={{ width: "100%" }}
        label={t("EndDate")}
        onAccept={(value) => handleEndDate(value)}
        slotProps={{
          textField: {
            size: "small",
            variant: variant,
            ...(testId && { inputProps: { "data-testid": `${testId}.end-input` } }),
          },
          actionBar: { actions: ["clear", "cancel", "accept"] },
          ...(testId && {
            previousIconButton: { "data-testid": `${testId}.end-input.prev-month` },
            nextIconButton: { "data-testid": `${testId}.end-input.next-month` },
            day: (ownerState) => ({
              "data-testid": `${testId}.end-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
            }),
          }),
        }}
      />
    </Stack>
  );
}
